import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * The account block at the foot of the Y-axis — identity and role, from the
 * session, and the only way out of the application.
 *
 * C5 IS THE WHOLE POINT OF THIS COMPONENT
 * =======================================
 * `design/system/shell.js` line 29 hardcodes the founder's first name and the
 * word Founder directly into the markup, and there is no role logic anywhere in
 * the design package, which contradicts §3.1. That file is a reference artefact and is not edited.
 * What C5 means here is: do not port it. Every value below comes from
 * `GET /auth/me` by way of the application's AuthProvider — name, role, and
 * picture — and there is no fallback that names anybody. Step 2's Definition of
 * Done greps this directory for that operator name as a literal; it appears
 * nowhere in this tree, not even in a comment, so the grep returns nothing.
 *
 * The initial is derived from the name rather than stored, so it cannot drift
 * from it. With no name yet — the moment before /auth/me resolves — the chip
 * renders its own skeleton rather than a guess.
 *
 * SIGN OUT, AND WHY THE HANDLER ARRIVES AS A PROP
 * ===============================================
 * Until V2-SIGNOUT there was no way to leave a v1.1 session except closing the
 * tab. The control belongs here because this is where an operator looks for it.
 *
 * The chip does NOT reach for `useAuth()` itself. `useAuth` lives in `App.js`,
 * and importing it here would pull the entire 4,900-line v1 application — and
 * every component it imports — into any test that renders the shell. The whole
 * point of `V2Routes.jsx` holding the only `../App` import is that the shell's
 * own modules stay renderable on their own. So the handler comes down as
 * `onSignOut`, from the one component that already has the session.
 *
 * With no handler the chip stays exactly what it was: a static block. A control
 * that cannot do the thing it names is worse than no control, so it is absent
 * rather than inert.
 */
function initialOf(name) {
  const trimmed = String(name || "").trim();
  return trimmed ? trimmed[0].toUpperCase() : "";
}

/** Presentational role label. The gate is the server's; this is the caption. */
function roleLabel(role) {
  if (!role) return "";
  return String(role)
    .toLowerCase()
    .split("_")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

export default function AccountChip({ user, onSignOut }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const rootRef = useRef(null);

  // Close on Escape or on a click anywhere outside the chip. Both listeners are
  // only attached while the menu is open — a closed menu costs nothing.
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  const handleSignOut = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onSignOut();
    } finally {
      // If the handler navigates away this never runs, which is fine. If it
      // fails and leaves us here, the button must be usable again.
      setBusy(false);
    }
  }, [busy, onSignOut]);

  if (!user) {
    return (
      <div className="acct acct-loading" aria-hidden="true">
        <span className="avatar skeleton" />
        <div className="acct-text">
          <span className="skeleton acct-skeleton-name" />
          <span className="skeleton acct-skeleton-role" />
        </div>
      </div>
    );
  }

  const name = user.name || user.operator_id || "";
  const role = roleLabel(user.role);
  const title = role ? `${name} · ${role}` : name;

  const identity = (
    <>
      {user.picture ? (
        <img className="avatar avatar-img" src={user.picture} alt="" />
      ) : (
        <span className="avatar" aria-hidden="true">
          {initialOf(name)}
        </span>
      )}
      <div className="acct-text">
        <span className="acct-name">{name}</span>
        {role ? <span className="acct-role dim">{role}</span> : null}
      </div>
    </>
  );

  if (typeof onSignOut !== "function") {
    return (
      <div className="acct" title={title}>
        {identity}
      </div>
    );
  }

  return (
    <div className="acct-root" ref={rootRef}>
      {open ? (
        <div className="acct-menu" role="menu" aria-label="Account">
          <button
            type="button"
            role="menuitem"
            className="acct-menu-item"
            data-testid="v2-signout-btn"
            onClick={handleSignOut}
            disabled={busy}
          >
            {busy ? "Signing out…" : "Sign out"}
          </button>
        </div>
      ) : null}

      <button
        type="button"
        className={open ? "acct acct-button on" : "acct acct-button"}
        data-testid="v2-account-chip"
        title={title}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {identity}
        <span className="acct-caret" aria-hidden="true">
          <svg viewBox="0 0 16 16" width="12" height="12" fill="none">
            <path
              d="M4 10l4-4 4 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
    </div>
  );
}
